import { Controller } from "@hotwired/stimulus"

// Drag-to-reorder for the editor's section sidebar. The default order
// comes from the server (EditorController::SECTION_KEYS); a user can
// drag any row to a new slot and that custom order is saved to
// localStorage under STORAGE_KEY. On connect we apply the saved order
// to the rendered <li> nodes so subsequent visits open with the user's
// arrangement.
//
// Reordering DOES NOT update the wizard's hardcoded SECTIONS array
// here — instead we fire `pcpm:sidebar-order-changed` so the wizard's
// connect() (which already reads the editor's data attributes as the
// source of truth for the active section) can resync.

const STORAGE_KEY = "pcpm.sidebar_order_v1"

export default class extends Controller {
  static targets = ["list", "row"]

  connect() {
    this._applySavedOrder()
    this._renumber()
  }

  start(event) {
    const row = event.currentTarget
    if (!row) return
    this._draggingKey = row.dataset.sectionKey
    row.classList.add("is-dragging")
    // Use a single key so all drop targets accept this drag
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move"
      event.dataTransfer.setData("text/plain", this._draggingKey || "")
    }
  }

  over(event) {
    if (!this._draggingKey) return
    event.preventDefault()
    const target = event.currentTarget
    if (!target || target.classList.contains("is-dragging")) return
    const rect = target.getBoundingClientRect()
    const before = (event.clientY - rect.top) < rect.height / 2
    const draggingNode = this.rowTargets.find(r => r.dataset.sectionKey === this._draggingKey)
    if (!draggingNode) return
    if (before) target.parentNode.insertBefore(draggingNode, target)
    else        target.parentNode.insertBefore(draggingNode, target.nextSibling)
  }

  drop(event) {
    event.preventDefault()
  }

  end(event) {
    const row = event.currentTarget
    if (row) row.classList.remove("is-dragging")
    this._draggingKey = null
    this._saveOrder()
    this._renumber()
    document.dispatchEvent(new CustomEvent("pcpm:sidebar-order-changed", {
      detail: { order: this._currentOrder() }
    }))
  }

  // Repaint each row's leading section number to its 1-based position in
  // the current DOM order. Called after the saved order is applied on
  // connect and after every drag, so the numbers always match what the
  // user sees (and stay in lock-step with the goal card's "12.1" badge,
  // which counts these same rows).
  _renumber() {
    if (!this.hasListTarget) return
    Array.from(this.listTarget.querySelectorAll("li[data-section-key]"))
      .forEach((li, i) => {
        const el = li.querySelector(".en-side-num")
        if (el) el.textContent = String(i + 1)
      })
  }

  // Read every row's section key in DOM order. The PDF/Preview link
  // is rendered as an <a> inside its <li>, but the <li> itself still
  // carries data-section-key="review" so it round-trips with the rest.
  _currentOrder() {
    if (!this.hasListTarget) return []
    return Array.from(this.listTarget.querySelectorAll("li[data-section-key]"))
      .map(li => li.dataset.sectionKey)
      .filter(Boolean)
  }

  _saveOrder() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this._currentOrder()))
    } catch (_) { /* quota / disabled — ignore */ }
  }

  // On connect: read saved order, then move each <li> to its saved
  // position. Any rows the saved list doesn't mention (newly added
  // sections shipped after the user customized) stay where the server
  // put them — they trail at the bottom in their original DOM order.
  _applySavedOrder() {
    if (!this.hasListTarget) return
    let saved
    try { saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]") } catch (_) { saved = [] }
    if (!Array.isArray(saved) || !saved.length) return
    const list = this.listTarget
    const byKey = new Map()
    list.querySelectorAll("li[data-section-key]").forEach(li => byKey.set(li.dataset.sectionKey, li))
    // Re-append in saved order. Missing keys are skipped; unsaved
    // keys remain at their current positions since detach+reattach
    // would lose them.
    saved.forEach(key => {
      const li = byKey.get(key)
      if (li) list.appendChild(li)
    })
    // Any rows NOT mentioned by the saved order get appended at the
    // end after the saved ones so newly-shipped sections still appear.
    list.querySelectorAll("li[data-section-key]").forEach(li => {
      if (!saved.includes(li.dataset.sectionKey)) list.appendChild(li)
    })
  }
}
